import { useState, useMemo } from "react";
import { ArrowUpRight, Search, X } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { PageHero, CtaSection } from "../components/common";
import { NEWS } from "../data/site";
import { Input } from "../components/ui/input";

const CATEGORIES = ["All", ...Array.from(new Set(NEWS.map((n) => n.category)))];

export default function News() {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return NEWS.filter((n) => {
      const inCat = category === "All" || n.category === category;
      const inQuery = !q || `${n.title} ${n.excerpt} ${n.category}`.toLowerCase().includes(q);
      return inCat && inQuery;
    });
  }, [category, query]);

  const showLead = category === "All" && !query.trim();
  const [lead, ...rest] = filtered;
  const gridItems = showLead ? rest : filtered;

  return (
    <>
      <Seo
        title="News & Insights | STERILTRON"
        description="The latest news, product announcements and engineering insights from STERILTRON."
        path="/news"
      />
      <PageHero
        label="News & Insights"
        titleLines={["The Latest", "from STERILTRON"]}
        description="Company updates, product announcements and engineering perspectives from our team in Hungary."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-12">
            <div className="flex flex-wrap gap-3">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`news-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setCategory(c)}
                  className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${
                    category === c ? "bg-brand-800 text-white border-brand-800" : "bg-white text-slate-600 border-slate-300 hover:border-brand-800 hover:text-brand-800"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative lg:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="news-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-9 pr-9 h-11 rounded-none border-slate-300 focus-visible:ring-brand-800 focus-visible:ring-offset-0"
              />
              {query && (
                <button data-testid="news-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-800">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {filtered.length === 0 && (
            <div data-testid="news-empty" className="border border-slate-200 bg-brand-50/40 p-16 text-center">
              <p className="font-heading text-2xl font-semibold text-brand-950">No articles found</p>
              <p className="mt-3 text-slate-600">Try a different category or search term.</p>
            </div>
          )}

          {/* Lead */}
          {showLead && lead && (
            <Reveal>
              <Link to={`/news/${lead.slug}`} className="group grid lg:grid-cols-2 gap-8 lg:gap-12 items-center bg-white border border-slate-200 hover:border-brand-800 transition-colors" data-testid="news-lead">
                <div className="overflow-hidden">
                  <img src={lead.image} alt={lead.title} className="w-full h-72 lg:h-[420px] object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-8 lg:p-12">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-widest">
                    <span className="text-brand-700 font-semibold">{lead.category}</span>
                    <span className="text-slate-300">/</span>
                    <span className="text-slate-500">{lead.date}</span>
                  </div>
                  <h2 className="mt-5 font-heading text-3xl md:text-4xl font-bold tracking-tighter text-brand-950 text-balance">{lead.title}</h2>
                  <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">{lead.excerpt}</p>
                  <span className="mt-8 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800 group-hover:gap-2.5 transition-all">Read Article <ArrowUpRight size={14} /></span>
                </div>
              </Link>
            </Reveal>
          )}

          {/* Grid */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${showLead && lead ? "mt-6" : ""}`}>
            {gridItems.map((n, i) => (
              <Reveal key={n.slug} delay={(i % 3) * 0.08}>
                <Link to={`/news/${n.slug}`} className="group block bg-white border border-slate-200 hover:border-brand-800 transition-colors h-full" data-testid={`news-card-${n.slug}`}>
                  <div className="overflow-hidden">
                    <img src={n.image} alt={n.title} loading="lazy" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-7">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-widest">
                      <span className="text-brand-700 font-semibold">{n.category}</span>
                      <span className="text-slate-300">/</span>
                      <span className="text-slate-500">{n.date}</span>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-semibold tracking-tight text-brand-950 leading-snug">{n.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{n.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
